import React from "react";
import { Link } from "react-router-dom";
import { Lock, ArrowRight } from "lucide-react";

// Modal shown when a paid user clicks a module locked by the
// sequential-progression rule, the bonus-modules rule, OR the Menu
// Directory plan gate (admin restricted the module to a different plan).
//
// `gate` is the `locked_reason` payload returned from /api/modules
// (or the `detail` body from a 403 on /api/modules/{slug}):
//   { kind: "sequential_core", current_number, unlock_after_number,
//     unlock_after_slug, unlock_after_name, message }
//   { kind: "bonus_gated", total_cores, cores_completed, message }
//   { kind: "plan_gated" }        — Menu Directory admin restriction
export default function ModuleLockedModal({ gate, onClose, linkPrefix = "/modules" }) {
  if (!gate) return null;
  const isBonus = gate.kind === "bonus_gated";
  const isPlanGated = gate.kind === "plan_gated";
  const heading = isPlanGated ? "Upgrade to Unlock"
    : isBonus ? "Bonus Modules Locked"
    : "Module Locked";
  const body = isPlanGated
    ? "This module is included on a higher plan. Upgrade your subscription to unlock it."
    : isBonus
    ? "Complete all 10 Core Modules to unlock the Bonus Modules."
    : `Complete Module ${gate.unlock_after_number} to unlock Module ${gate.current_number}.`;
  return (
    <div
      className="fixed inset-0 z-[9500] flex items-center justify-center p-4 bg-shell-side/80 backdrop-blur-md"
      onClick={onClose}
      data-testid="module-locked-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-3xl bg-white shadow-2xl ring-1 ring-shell-border p-8"
      >
        <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary grid place-items-center">
          <Lock className="w-5 h-5" />
        </div>
        <h3 className="mt-4 font-display text-2xl leading-tight" data-testid="module-locked-heading">
          {heading}
        </h3>
        <p className="mt-3 text-sm text-muted-foreground" data-testid="module-locked-body">
          {body}
        </p>
        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="module-locked-close"
            className="text-sm font-semibold text-muted-foreground hover:text-foreground px-4 py-2 rounded-full border border-shell-border"
          >
            Close
          </button>
          {isPlanGated && (
            <Link
              to="/account/subscription"
              onClick={onClose}
              data-testid="module-locked-upgrade"
              className="btn-primary text-sm"
            >
              Upgrade plan <ArrowRight className="w-3 h-3" />
            </Link>
          )}
          {!isBonus && !isPlanGated && (
            <Link
              to={`${linkPrefix}/${gate.unlock_after_slug}`}
              onClick={onClose}
              data-testid="module-locked-goto"
              className="btn-primary text-sm"
            >
              Go to Module {gate.unlock_after_number} <ArrowRight className="w-3 h-3" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
